import http from './http.ts';

export type Hotel = {
  id?: number;
  tenantId: number;
  name: string;
  city: string;
  address?: string;
  stars?: number;
  amenities?: string;
  coverImageUrl?: string;
  brochurePdfUrl?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type RoomType = {
  id?: number;
  hotelId: number;
  name: string;
  capacityAdults?: number;
  capacityChildren?: number;
  bedType?: string;
  basePrice: number;
};

const base = '/api/v1/admin/hotels';

export const getHotels = async (): Promise<Hotel[]> => {
  return http.get(base);
};

export type CreateHotelPayload = {
  name: string;
  city: string;
  tenantId: number;
  address?: string;
  stars?: number;
  amenities?: string;
};

export const createHotel = async (payload: CreateHotelPayload): Promise<Hotel> => {
  return http.post(base, payload, { headers: { 'X-Tenant-Id': String(payload.tenantId) } });
};

export const updateHotel = async (id: number, payload: Partial<Hotel>): Promise<Hotel> => {
  return http.put(`${base}/${id}`, payload);
};

export const deleteHotel = async (id: number): Promise<void> => {
  await http.del(`${base}/${id}`);
};

export const getHotelDetails = async (id: number): Promise<Hotel> => {
  const hotels = await getHotels();
  const match = hotels.find((hotel) => hotel.id === id);
  if (!match) {
    throw new Error(`Hotel ${id} not found`);
  }
  return match;
};

export const uploadCover = async (hotelId: number, file: File): Promise<string> => {
  const fd = new FormData();
  fd.append('file', file);
  const r = await http.upload(`${base}/${hotelId}/images`, fd);
  return (r.url as string);
};

export const uploadBrochure = async (hotelId: number, file: File): Promise<string> => {
  const fd = new FormData();
  fd.append('file', file);
  const r = await http.upload(`${base}/${hotelId}/brochure`, fd);
  return (r.url as string);
};

export const uploadImagesBulk = async (hotelId: number, files: File[]): Promise<string[]> => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const r = await http.upload(`${base}/${hotelId}/images/bulk`, fd);
  return (r.urls as string[]);
};

export const deleteImage = async (hotelId: number, imageId: number): Promise<void> => {
  await http.del(`${base}/${hotelId}/images/${imageId}`);
};

export const reorderImages = async (hotelId: number, items: { id: number; sortOrder: number }[]): Promise<void> => {
  await http.patch(`${base}/${hotelId}/images/reorder`, items);
};

// Promos
export type Promo = {
  id?: number;
  code: string;
  type: 'PERCENT' | 'FIXED';
  value: number;
  active?: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  minNights?: number | null;
};

export const listPromos = async (hotelId: number): Promise<Promo[]> => {
  return http.get(`${base}/${hotelId}/promos`);
};

export const createPromo = async (hotelId: number, payload: Promo): Promise<Promo> => {
  return http.post(`${base}/${hotelId}/promos`, payload);
};

export const updatePromo = async (hotelId: number, id: number, payload: Promo): Promise<Promo> => {
  return http.put(`${base}/${hotelId}/promos/${id}`, payload);
};

export const deletePromo = async (hotelId: number, id: number): Promise<void> => {
  await http.del(`${base}/${hotelId}/promos/${id}`);
};

// Extras
export type Extra = {
  id?: number;
  code: string;
  name: string;
  priceType: 'PER_NIGHT' | 'PER_STAY' | 'PER_PERSON';
  price: number;
  active?: boolean;
};

export const listExtras = async (hotelId: number): Promise<Extra[]> => {
  return http.get(`${base}/${hotelId}/extras`);
};

export const createExtra = async (hotelId: number, payload: Extra): Promise<Extra> => {
  return http.post(`${base}/${hotelId}/extras`, payload);
};

export const updateExtra = async (hotelId: number, id: number, payload: Extra): Promise<Extra> => {
  return http.put(`${base}/${hotelId}/extras/${id}`, payload);
};

export const deleteExtra = async (hotelId: number, id: number): Promise<void> => {
  await http.del(`${base}/${hotelId}/extras/${id}`);
};

// Room types
export const createRoomType = async (hotelId: number, payload: Partial<RoomType>): Promise<RoomType> => {
  return http.post(`${base}/${hotelId}/room-types`, payload);
};

export const updateRoomType = async (hotelId: number, id: number, payload: Partial<RoomType>): Promise<RoomType> => {
  return http.put(`${base}/${hotelId}/room-types/${id}`, payload);
};

export const deleteRoomType = async (hotelId: number, id: number): Promise<void> => {
  await http.del(`${base}/${hotelId}/room-types/${id}`);
};

export const listRoomTypes = async (hotelId: number): Promise<RoomType[]> => {
  return http.get(`${base}/${hotelId}/room-types`);
};

// For listing room types we leverage public details which include rooms
export const getHotelDetailsPublic = async (id: number): Promise<{ hotel: Hotel; roomTypes: RoomType[] } | any> => {
  return http.get(`/api/v1/public/hotels/${id}`);
};


